import { useCallback, useMemo, useState } from 'react';

import { useSession } from '@ais-ui/websockets';
import { useCredentials } from '@ais-ui/auth';
import { useBasePath } from '@ais-ui/custom-hooks';

import { getBasePath } from './getAISBasePath';
import {
  Configuration,
  DateTimeRangeSchema,
  FeatureCollection,
  GpsLostApi,
  GridJobConfigSchemaInput,
  GridJobConfigSchemaOutput,
  GridJobInstanceSchema,
  PageGridJobConfigSchema,
  PageGridJobInstanceSchema,
} from '@ais-ui/api-spec/gpslost';
import {
  UseMutationResult,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query';

import {
  useTrackedMutation as useMutation,
  useTrackedQuery as useQuery,
} from '@ais-ui/performance-tracking';

import {
  convertFieldsToDates,
  getAuthHeaderFromCredentials,
} from '@ais-ui/utils';
import { useApiClientWrapper } from './apiClientWrapper';

const useGPSLostApi = () => {
  const credentials = useCredentials();
  const { sessionId } = useSession();
  const { protocol, hostname } = useBasePath();
  const apiClientWrapper = useApiClientWrapper();

  let basePath = getBasePath();
  let configuration: Configuration;

  const username = credentials?.username || '';
  const password = credentials?.password || '';

  const authHeader = getAuthHeaderFromCredentials(username, password);

  if (hostname.includes('localhost')) {
    configuration = new Configuration({
      basePath: `${basePath}api/ingress/ais-gps-lost-detection`,
      headers: {
        'X-Application-Name': 'marinepulse',
        Authorization: authHeader,
      },
    });
  } else {
    basePath = `${basePath}aisweb/api/ingress/ais-gps-lost-detection`;
    configuration = new Configuration({
      basePath: basePath,
      headers: {
        'X-Application-Name': 'marinepulse',
        'Aisweb-Session': sessionId,
      },
    });
  }

  const gpsLostApi = useMemo(
    () => new GpsLostApi(configuration),
    [configuration]
  );
  return apiClientWrapper(gpsLostApi) as GpsLostApi;
};

export const useCreateGPSLostJobConfig = (): UseMutationResult<
  GridJobConfigSchemaOutput,
  Error,
  GridJobConfigSchemaInput,
  unknown
> => {
  const gpsLostApi = useGPSLostApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (configInput: GridJobConfigSchemaInput) => {
      const response = await gpsLostApi.createGridJobConfigGridJobConfigsPost({
        gridJobConfigSchemaInput: configInput,
      });
      return response;
    },
    onSuccess: () => {
      // Invalidate both query keys to ensure all caches are updated
      queryClient.invalidateQueries({ queryKey: ['allGridJobConfigs'] });
      queryClient.invalidateQueries({ queryKey: ['gridJobConfigs'] });
    },
  }, 'gpslost:create_job_config');
};

/**
 * Custom hook to fetch all grid job configurations across all pages
 * @param options - React-query and pagination options
 * @returns Query result containing all grid job configurations
 */
export const useAllGridJobConfigs = (options?: {
  enabled?: boolean;
  refetchInterval?: number | false;
  staleTime?: number;
  pageSize?: number;
  onProgress?: (currentPage: number, totalItems: number) => void;
}): UseQueryResult<PageGridJobConfigSchema, Error> => {
  const gpsLostApi = useGPSLostApi();

  return useQuery({
    queryKey: ['allGridJobConfigs'],
    queryFn: async () => {
      const pageSize = options?.pageSize || 50;
      let allItems: any[] = [];
      let currentPage = 1;
      let hasMorePages = true;
      let firstResponse: PageGridJobConfigSchema | null = null;

      while (hasMorePages) {
        const response = await gpsLostApi.getGridJobConfigsGridJobConfigsGet({
          page: currentPage,
          size: pageSize,
        });

        if (!firstResponse) {
          firstResponse = response;
        }

        if (response.items && response.items.length > 0) {
          allItems = [...allItems, ...response.items];

          // Call progress callback if provided
          options?.onProgress?.(currentPage, allItems.length);

          currentPage++;
          hasMorePages = response.items.length === pageSize;
        } else {
          hasMorePages = false;
        }
      }

      const allPagesResponse: PageGridJobConfigSchema = {
        ...firstResponse!,
        items: allItems,
        page: 1,
        size: allItems.length,
        total: allItems.length,
      };

      convertFieldsToDates(allPagesResponse, [
        'items.created_at',
        'items.modified_at',
        'items.started_at',
        'items.historical_range.lower',
        'items.historical_range.upper',
      ]);

      return allPagesResponse;
    },
    enabled: options?.enabled,
    refetchInterval: options?.refetchInterval,
    staleTime: options?.staleTime,
  }, 'gpslost:allJobConfigs');
};

/**
 * Custom hook to fetch all grid job configurations
 * @param page - The page number (optional, default is handled by API)
 * @param size - The number of items per page (optional, default is handled by API)
 * @param options - Additional react-query options
 * @returns Query result containing grid job configurations
 */
export const useGridJobConfigs = (
  page?: number,
  size?: number,
  options?: {
    enabled?: boolean;
    refetchInterval?: number | false;
    staleTime?: number;
  }
): UseQueryResult<PageGridJobConfigSchema, Error> => {
  const gpsLostApi = useGPSLostApi();

  return useQuery({
    queryKey: ['gridJobConfigs', page, size],
    queryFn: async () => {
      const response = await gpsLostApi.getGridJobConfigsGridJobConfigsGet({
        page,
        size,
      });
      convertFieldsToDates(response, [
        'items.created_at',
        'items.modified_at',
        'items.started_at',
        'items.historical_range.lower',
        'items.historical_range.upper',
      ]);
      return response;
    },
    ...options,
  }, 'gpslost:jobConfigs');
};

/**
 * Custom hook that returns a function to fetch grid job details by configuration ID
 * @returns A function that takes a jobId and returns a Promise with the job configuration
 */
export const useGetGridJobConfigById = () => {
  const gpsLostApi = useGPSLostApi();

  return useCallback(
    async (jobId: string | number): Promise<GridJobConfigSchemaOutput> => {
      try {
        const response =
          await gpsLostApi.getGridJobConfigGridJobConfigsJobIdGet({
            jobId,
          });
        return response;
      } catch (error) {
        console.error(
          `Error fetching grid job config with ID ${jobId}:`,
          error
        );
        throw error;
      }
    },
    [gpsLostApi]
  );
};

/**
 * Custom hook that returns a function to fetch grid job details by configuration ID
 * @returns A function that takes a jobId and returns a Promise with the job configuration
 */
export const useGetGridJobConfigJobInstancesByIdWithLoading = () => {
  const gpsLostApi = useGPSLostApi();
  const [isLoadingAllPages, setIsLoadingAllPages] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState({ page: 0, items: 0 });

  const fetchAllInstances = useCallback(
    async (jobId: string | number): Promise<PageGridJobInstanceSchema> => {
      setIsLoadingAllPages(true);
      setLoadingProgress({ page: 0, items: 0 });

      try {
        let allItems: any[] = [];
        let currentPage = 1;
        let hasMorePages = true;
        const pageSize = 50;
        let firstResponse: PageGridJobInstanceSchema | null = null;

        while (hasMorePages) {
          const response =
            await gpsLostApi.getGridJobInstancesGridJobConfigsJobIdInstancesGet(
              {
                jobId,
                page: currentPage,
                size: pageSize,
              }
            );

          if (!firstResponse) {
            firstResponse = response;
          }

          if (response.items && response.items.length > 0) {
            allItems = [...allItems, ...response.items];
            setLoadingProgress({ page: currentPage, items: allItems.length });

            currentPage++;
            hasMorePages = response.items.length === pageSize;
          } else {
            hasMorePages = false;
          }
        }

        const allPagesResponse: PageGridJobInstanceSchema = {
          ...firstResponse!,
          items: allItems,
          page: 1,
          size: allItems.length,
          total: allItems.length,
        };

        convertFieldsToDates(allPagesResponse, [
          'items.created_at',
          'items.modified_at',
          'items.finished_at',
          'items.timerange.lower',
          'items.timerange.upper',
        ]);

        return allPagesResponse;
      } catch (error) {
        console.error(
          `Error fetching all grid job config instances with ID ${jobId}:`,
          error
        );
        throw error;
      } finally {
        setIsLoadingAllPages(false);
      }
    },
    [gpsLostApi]
  );

  return {
    fetchAllInstances,
    isLoadingAllPages,
    loadingProgress,
  };
};

/**
 * Custom hook that returns a function to fetch grid job details by configuration ID
 * @returns A function that takes a jobId and returns a Promise with the job configuration
 */
export const useGetGridJobConfigGridById = () => {
  const gpsLostApi = useGPSLostApi();
  const queryClient = useQueryClient();

  return useCallback(
    async (jobId: string | number): Promise<FeatureCollection> => {
      const cacheKey = ['gridJobConfig', jobId];

      // Check cache first
      const cachedData = queryClient.getQueryData<FeatureCollection>(cacheKey);
      if (cachedData) {
        return cachedData;
      }

      try {
        const response =
          await gpsLostApi.getGridJobConfigGridJobConfigsJobIdHexagongridGet({
            jobId,
          });

        // Cache the response for 5 minutes
        queryClient.setQueryData(cacheKey, response, {
          updatedAt: Date.now(),
        });

        // Set stale time to 5 minutes (300,000 ms)
        queryClient.setQueryDefaults(cacheKey, {
          staleTime: 5 * 60 * 1000, // 5 minutes
          gcTime: 5 * 60 * 1000, // 5 minutes
        });

        return response;
      } catch (error) {
        console.error(
          `Error fetching grid job config hexagon grid with ID ${jobId}:`,
          error
        );
        throw error;
      }
    },
    [gpsLostApi, queryClient]
  );
};

/**
 * Custom hook that returns a function to fetch specific grid job instance details by job ID and instance ID
 * @returns A function that takes a jobId and instanceId and returns a Promise with the job instance details
 */
export const useGetGridJobInstanceById = () => {
  const gpsLostApi = useGPSLostApi();

  return useCallback(
    async (
      jobId: string | number,
      jobInstanceId: string | number
    ): Promise<GridJobInstanceSchema> => {
      try {
        const response =
          await gpsLostApi.getGridJobInstanceGridJobConfigsJobIdInstancesJobInstanceIdGet(
            {
              jobId,
              jobInstanceId,
            }
          );
        return response;
      } catch (error) {
        console.error(
          `Error fetching grid job instance with ID ${jobId} and instance ID ${jobInstanceId}:`,
          error
        );
        throw error;
      }
    },
    [gpsLostApi]
  );
};

/**
 * Custom hook that returns a function to fetch grid job results by configuration ID and instance ID
 * @returns A function that takes a jobId and instanceId and returns a Promise with the job results
 */
export const useGetGridJobResultsById = () => {
  const gpsLostApi = useGPSLostApi();
  const queryClient = useQueryClient();

  return useCallback(
    async (
      jobId: string | number,
      jobInstanceId: string | number
    ): Promise<any> => {
      const cacheKey = ['gridJobResults', jobId, jobInstanceId];

      // Check cache first
      const cachedData = queryClient.getQueryData(cacheKey);
      if (cachedData) {
        return cachedData;
      }

      try {
        const response =
          await gpsLostApi.getGridJobResultsGridJobResultsJobIdResultsJobInstanceIdGet(
            {
              jobId,
              jobInstanceId,
            }
          );

        convertFieldsToDates(response, ['timerange.lower', 'timerange.upper']);

        // Cache the response for 5 minutes
        queryClient.setQueryData(cacheKey, response);

        // Set stale time to 5 minutes
        queryClient.setQueryDefaults(cacheKey, {
          staleTime: 5 * 60 * 1000, // 5 minutes
          gcTime: 5 * 60 * 1000, // 5 minutes
        });

        return response;
      } catch (error) {
        console.error(
          `Error fetching grid job results with ID ${jobId} and instance ID ${jobInstanceId}:`,
          error
        );
        throw error;
      }
    },
    [gpsLostApi, queryClient]
  );
};

/**
 * Custom hook that returns a function to fetch grid job tracks by job ID, instance ID, and optional MMSI
 * @returns A function that takes a jobId, jobInstanceId, and optional mmsi and returns a Promise with the job tracks
 */
export const useGetGridJobTracks = () => {
  const gpsLostApi = useGPSLostApi();

  return useCallback(
    async (
      jobId: string | number,
      jobInstanceId: string | number,
      mmsi?: string | number
    ): Promise<any> => {
      try {
        const response =
          await gpsLostApi.getGridJobTracksGridJobResultsJobIdTracksJobInstanceIdGet(
            {
              jobId,
              jobInstanceId,
              mmsi,
            }
          );

        convertFieldsToDates(response, [
          'items.created_at',
          'items.modified_at',
          'items.bs_ts',
          'items.eta',
        ]);

        return response;
      } catch (error) {
        console.error(
          `Error fetching grid job tracks with ID ${jobId}, instance ID ${jobInstanceId}${
            mmsi ? `, and MMSI ${mmsi}` : ''
          }:`,
          error
        );
        throw error;
      }
    },
    [gpsLostApi]
  );
};

/**
 * Custom hook to delete a grid job configuration by ID
 * @returns A mutation object that handles deleting a grid job configuration
 */
export const useDeleteGridJobConfig = (): UseMutationResult<
  void,
  Error,
  string | number,
  unknown
> => {
  const gpsLostApi = useGPSLostApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (jobId: string | number): Promise<void> => {
      try {
        await gpsLostApi.deleteGridJobConfigGridJobConfigsJobIdDelete({
          jobId,
        });
      } catch (error) {
        console.error('Failed to delete grid job config:', error);
        throw error;
      }
    },
    onSuccess: () => {
      // Invalidate both query keys to ensure all caches are updated
      queryClient.invalidateQueries({ queryKey: ['allGridJobConfigs'] });
      queryClient.invalidateQueries({ queryKey: ['gridJobConfigs'] });
    },
  }, 'gpslost:delete_job_config');
};

/**
 * Custom hook to fetch the date range of stat voy pos dates
 * @param options - Additional react-query options
 * @returns Query result containing the date range
 */
export const useStatVoyPosReportsDaterange = (options?: {
  enabled?: boolean;
  refetchInterval?: number | false;
  staleTime?: number;
}): UseQueryResult<DateTimeRangeSchema, Error> => {
  const gpsLostApi = useGPSLostApi();

  return useQuery({
    queryKey: ['statVoyPosReportsDaterange'],
    queryFn: async () => {
      const response = await gpsLostApi.getStatVoyPosReportsDaterange();
      convertFieldsToDates(response, ['lower', 'upper']);
      return response;
    },
    ...options,
  }, 'gpslost:statVoyPosDaterange');
};
